import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import {
    AgentCoaching,
    AgentCoachingStatus,
} from "../models/AgentModels/AgentCoaching";
import Question from "../models/Question";
import BaseService from "./BaseService";
import { EvalType } from "components/Conversations/Stores/ConversationsStore";
import { InteractionType } from "models/InteractionType";
import RootStore from "stores/RootStore";
import { AuthStore } from "stores/AuthStore";
import { fetch_retry } from "utils/FetchRetry";

export interface AgentCoachingDetails {
    label: string;
    value: string;
    note: string;
}
interface AgentCoachingResult {
    rows: any[];
    cols: any[];
    details: AgentCoachingDetails[];
}
export interface AgentEvaluations {
    all: AgentCoachingData[];
    coached: AgentCoachingData[];
    notCoached: AgentCoachingData[];
}
export interface AgentCoachingEvaluationScore {
    avgScore: number;
    evaluationId: string;
    moduleName: string;
}
export interface AgentCoachingData {
    id: string;
    evaluationQbId?: number;
    evaluationQbIds?: number[];
    evaluationIds?: string[];
    submittedDate?: string;
    agentCoachingId?: string;
    interactionDate?: string;
    coachedOn?: string;
    agentId?: string;
    coachId?: string;
    improvementNotes?: string;
    agentDoWellNotes?: string;
    focusAreas?: string;
    acknowledged?: boolean;
    responseNotes?: string;
    analystUserName?: string;
    firstName?: string;
    lastName?: string;
    scores?: AgentCoachingEvaluationScore[];
    coachingStatus?: AgentCoachingStatus;
    evalAcknowledged?: boolean;
    /**
     * Used to link to conversation for coaching review (amdId = conversationId)
     */
    amdId?: string;
    amdIds?: string[];
    isConversationOnly?: boolean;
    /**
     * Used to redirect to old coaching if InteractionType = Live
     */
    interactionType?: InteractionType;
    callDurationMillis?: number;
}

/**
 * The API serializes the entity's navigation property as `agentCoachingTargets`,
 * while the create/update request binds a `targets` array. Normalize responses so
 * the rest of the app only ever reads `targets`.
 */
function normalizeCoaching<T extends AgentCoaching>(coaching: T): T {
    const targets = coaching.targets ?? (coaching as any).agentCoachingTargets;
    if (targets) coaching.targets = targets;

    return coaching;
}

interface AgentCoachingTargetDetail {
    evaluationId?: string;
    evaluationQbId?: number;
    audioMetadataId?: string;
    interactionType?: InteractionType;
}

interface MyDraftsResponse {
    drafts: AgentCoaching[];
    targetDetails: AgentCoachingTargetDetail[];
}

export enum CoachingNoteLockResultStatus {
    Acquired = "Acquired",
    Locked = "Locked",
    AlreadyCoached = "AlreadyCoached",
}

interface CoachingNoteLockResponse {
    status: CoachingNoteLockResultStatus;
}

/**
 * Copies the display data MyDrafts returns alongside the drafts onto the targets it
 * belongs to, so nothing downstream has to look an evaluation up in another dataset.
 */
function attachTargetDetails(
    draft: AgentCoaching,
    details: AgentCoachingTargetDetail[],
): AgentCoaching {
    for (const target of draft.targets ?? []) {
        const detail = target.evaluationId
            ? details.find((d) => d.evaluationId === target.evaluationId)
            : details.find(
                  (d) =>
                      !d.evaluationId &&
                      d.audioMetadataId === target.audioMetadataId,
              );
        if (!detail) continue;

        target.evaluationQbId = detail.evaluationQbId;
        target.interactionType = detail.interactionType;
        // Only an evaluation target needs the conversation resolved for it; a
        // conversation target already carries its own id.
        if (target.evaluationId)
            target.evaluationAudioMetadataId = detail.audioMetadataId;
    }

    return draft;
}

export class AgentCoachingService extends BaseService {
    protected urlBase: string = "api/AgentCoaching";

    public async getAgents(
        startDate: string,
        endDate: string,
        dateReference?: DateReferenceOption,
        showMachineEvals?: boolean,
        agentId?: string,
    ) {
        const body = {
            startDate,
            endDate,
        };

        if (dateReference) {
            body["dateReference"] = dateReference;
        }

        if (!showMachineEvals) {
            body["evalType"] = EvalType.BottomOfTheFunnel;
        }

        if (agentId) {
            body["agentId"] = agentId;
        }

        const res = await this.post<AgentCoachingResult>(
            JSON.stringify(body),
            new URLSearchParams({}),
            "Agents",
        );

        return res;
    }

    public async getAgentEvaluations(
        agentId: string,
        startDate: string,
        endDate: string,
        dateReference?: DateReferenceOption,
        isAgentAsUser?: boolean,
        hierarchyIds?: string[],
        showMachineEvals?: boolean,
    ) {
        const params = new URLSearchParams();

        const body = {
            agentId: agentId,
            startDate: startDate,
            endDate: endDate,
            dateReference: dateReference,
            hierarchyIds: hierarchyIds,
            isAgentAsUser: isAgentAsUser?.toString(),
            evalType: showMachineEvals ? undefined : "BottomOfTheFunnel",
        };

        const res = await this.post<AgentEvaluations>(
            JSON.stringify(body),
            params,
            "AgentEvaluations",
        );
        return res;
    }

    public async getMyDrafts(agentId: string) {
        const params = new URLSearchParams({ agentId });
        const res = await this.get<MyDraftsResponse>(params, "MyDrafts");

        return res.drafts
            .map(normalizeCoaching)
            .map((draft) => attachTargetDetails(draft, res.targetDetails));
    }

    public async deleteDrafts(draftIds: string[]) {
        const params = new URLSearchParams();
        return this.delete(
            params,
            "DeleteDrafts",
            JSON.stringify({ draftIds }),
        );
    }

    public async getEvaluationQuestions() {
        const params = new URLSearchParams({});
        const res = await this.get<Question[]>(params, "EvaluationQuestions");
        return res;
    }

    public async acknowledgeAgentCoaching(agentCoaching: AgentCoaching) {
        const params = new URLSearchParams();
        const res = await this.post(
            JSON.stringify(agentCoaching),
            params,
            "UpdateAgentCoaching",
        );
        return res;
    }

    public async createCoachingAsCompleted(agentCoaching: AgentCoaching) {
        agentCoaching.coachingStatus = AgentCoachingStatus.Completed;
        const params = new URLSearchParams();
        const res = await this.post<AgentCoaching>(
            JSON.stringify(agentCoaching),
            params,
            "AgentCoaching",
        );
        return res;
    }

    public async createCoachingAsDraft(agentCoaching: AgentCoaching) {
        agentCoaching.coachingStatus = AgentCoachingStatus.InProgress;

        const params = new URLSearchParams();

        const res = await this.post<AgentCoaching>(
            JSON.stringify(agentCoaching),
            params,
            "AgentCoaching",
        );
        return res;
    }

    public async putCoachingDraft(agentCoaching: AgentCoaching) {
        return this.put<AgentCoaching>(
            new URLSearchParams(),
            "UpdateAgentCoachingDraft",
            JSON.stringify(agentCoaching),
        );
    }

    public async getModuleScoresForEvaluations(evaluationIds: any[]) {
        const params = new URLSearchParams();
        const res = await this.post(
            JSON.stringify({
                evaluationIds: evaluationIds,
            }),
            params,
            "ModuleScoresForEvaluations",
        );
        return res;
    }

    public async getModuleScoreForEvaluations(evaluationIds: any[]) {
        const params = new URLSearchParams();
        const res = await this.post(
            JSON.stringify({
                evaluationIds: evaluationIds,
            }),
            params,
            "ModuleScoreForEvaluations",
        );
        return res;
    }

    public async getCoachingByEvaluation(evaluationId: string) {
        const params = new URLSearchParams({ evaluationId });
        const res = await this.get<AgentCoaching[]>(params, "ByEvaluation");
        return res.map(normalizeCoaching);
    }

    public async getCoachingByConversation(conversationId: string) {
        const params = new URLSearchParams({ conversationId });
        const res = await this.get<AgentCoaching[]>(params, "ByConversation");
        return res.map(normalizeCoaching);
    }

    public async tryGetCoachingNoteLock(
        agentId: string,
        resourceIds: string[],
    ): Promise<CoachingNoteLockResultStatus> {
        const params = new URLSearchParams({ agentId });
        resourceIds.forEach((resourceId) => {
            params.append("resourceIds", resourceId);
        });

        const res = await this.get<CoachingNoteLockResponse | boolean>(
            params,
            "GetCoachingNoteLock",
        );

        if (typeof res === "boolean") {
            return res
                ? CoachingNoteLockResultStatus.Acquired
                : CoachingNoteLockResultStatus.Locked;
        }

        return res.status ?? (res as any).Status;
    }

    public async releaseCoachingNoteLock(
        agentId: string,
        resourceIds: string[],
    ) {
        const urlParams = new URLSearchParams({ agentId });
        resourceIds.forEach((resourceId) => {
            urlParams.append("resourceIds", resourceId);
        });
        let headers = RootStore().getStore(AuthStore).getRequestHeader();
        headers = { ...headers, "api-version": this.apiVersion };

        // Using fetch directly because the base service will debounce the
        // request and the browser may cancel it when unloading the page.
        return fetch_retry(
            `/${this.urlBase}/ReleaseCoachingNoteLock?${urlParams}`,
            {
                method: "DELETE",
                headers,
                keepalive: true,
            },
            0,
        );
    }
}
